import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, ShoppingCart, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';

const getLocalDateString = () => {
  const today = new Date();
  const localOffset = today.getTimezoneOffset() * 60000;
  const localDate = new Date(today.getTime() - localOffset);
  return localDate.toISOString().split('T')[0];
};

const formatPurchaseDate = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;
  const [year, month, day] = parts;
  const dateObj = new Date(year, month - 1, day);
  return dateObj.toLocaleDateString('pt-BR');
};

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    date: getLocalDateString(),
    supplier_id: '',
    supplier_name: '',
    notes: ''
  });
  
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, suppliersRes, productsRes] = await Promise.all([
        api.purchases?.getAll ? api.purchases.getAll() : { data: [] },
        api.suppliers?.getAll ? api.suppliers.getAll() : { data: [] },
        api.products.getAll()
      ]);
      setPurchases(purchasesRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados. Verifique sua conexão.');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = (value) => {
    const selected = suppliers.find(s => s.id === value);
    if (selected) {
      setFormData({
        ...formData,
        supplier_id: selected.id,
        supplier_name: selected.name
      });
    }
  };

  const addItem = () => {
    setSelectedItems([
      ...selectedItems,
      { product_id: '', product_name: '', quantity: 1, unit_cost: 0, total: 0 }
    ]);
  };

  const removeItem = (index) => {
    const newItems = [...selectedItems];
    newItems.splice(index, 1);
    setSelectedItems(newItems);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...selectedItems];
    const item = newItems[index];

    if (field === 'product_id') {
      const prod = products.find(p => p.id === value);
      if (prod) {
        item.product_id = prod.id;
        item.product_name = prod.name;
        // Puxa o último preço de custo conhecido para facilitar
        item.unit_cost = prod.cost_price || 0; 
      }
    } else {
      item[field] = value;
    }

    // Recalcula o total da linha
    item.quantity = parseFloat(item.quantity) || 0;
    item.unit_cost = parseFloat(item.unit_cost) || 0;
    item.total = item.quantity * item.unit_cost;

    setSelectedItems(newItems);
  };

  const calculateTotalCost = () => {
    return selectedItems.reduce((acc, item) => acc + (item.total || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      toast.error('Adicione pelo menos um produto na compra.');
      return;
    }

    const invalidItems = selectedItems.filter(i => !i.product_id || i.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error('Verifique os itens. Produto e quantidade válidos são obrigatórios.');
      return;
    }

    // A trava de segurança corporativa
    if (!formData.supplier_id) {
      toast.error('Selecione um fornecedor cadastrado na lista.');
      return;
    }

    try {
      const purchaseData = {
        ...formData,
        total_cost: calculateTotalCost(),
        items: selectedItems
      };
      
      await api.purchases.create(purchaseData);
      toast.success('Compra registrada! Estoque e Custos atualizados.');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar a compra.');
    }
  };

  const resetForm = () => {
    setFormData({
      date: getLocalDateString(),
      supplier_id: '',
      supplier_name: '',
      notes: ''
    });
    setSelectedItems([]);
  };

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Entrada de Estoque</h1>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase">
              <Plus className="mr-2 h-5 w-5" /> Lançar Nota / Compra
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl uppercase font-bold">Registrar Compra</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary/30 p-4 border border-border rounded-md">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Data da Compra</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Fornecedor *</Label>
                  {suppliers.length > 0 ? (
                    <Select onValueChange={handleSupplierChange} value={formData.supplier_id || undefined}>
                      <SelectTrigger className="mt-1 font-mono border-border">
                        <SelectValue placeholder="Selecione na lista..." />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-1 flex items-center h-10 px-3 border border-destructive/50 rounded-md bg-destructive/10 text-destructive text-xs uppercase font-bold tracking-wider">
                      Cadastre fornecedores primeiro
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Observações (Nº da Nota, etc)</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="Opcional"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-primary" /> Itens da Compra
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-8 text-xs font-bold uppercase">
                    <Plus className="h-3 w-3 mr-1" /> Add Produto
                  </Button>
                </div>
                
                {selectedItems.length === 0 ? (
                  <div className="p-8 border-2 border-dashed border-border text-center text-muted-foreground bg-card">
                    Adicione os produtos que entraram no estoque.
                  </div>
                ) : (
                  <div className="border border-border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader className="bg-secondary">
                        <TableRow>
                          <TableHead className="text-xs uppercase">Produto</TableHead>
                          <TableHead className="text-xs uppercase w-24">Qtd</TableHead>
                          <TableHead className="text-xs uppercase w-32">Custo Un. (R$)</TableHead>
                          <TableHead className="text-xs uppercase w-32 text-right">Total</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedItems.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Select onValueChange={(val) => updateItem(index, 'product_id', val)} value={item.product_id || undefined}>
                                <SelectTrigger className="h-8 font-mono text-sm border-0 bg-transparent p-0 shadow-none focus:ring-0">
                                  <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                min="1" 
                                className="h-8 font-mono text-sm px-2"
                                value={item.quantity} 
                                onChange={(e) => updateItem(index, 'quantity', e.target.value)} 
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                step="0.01" 
                                className="h-8 font-mono text-sm px-2"
                                value={item.unit_cost} 
                                onChange={(e) => updateItem(index, 'unit_cost', e.target.value)} 
                              />
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-primary">
                              {item.total.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeItem(index)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-4 bg-primary/10 border-2 border-primary rounded-md">
                <p className="text-sm text-muted-foreground uppercase tracking-wider font-bold">Custo Total da Nota</p>
                <p className="text-3xl font-mono font-bold text-primary">
                  R$ {calculateTotalCost().toFixed(2)}
                </p>
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold uppercase h-12">
                Salvar Compra e Atualizar Estoque
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {purchases.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground uppercase tracking-widest text-sm">Nenhuma entrada de estoque registrada.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Data</TableHead>
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Fornecedor</TableHead>
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Itens Comprados</TableHead>
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Custo Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id} className="border-border/50 hover:bg-secondary/50">
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          {formatPurchaseDate(purchase.date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-bold text-foreground">{purchase.supplier_name}</p>
                        {purchase.notes && <p className="text-xs text-muted-foreground font-mono">{purchase.notes}</p>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {purchase.items.map(i => `${i.quantity}x ${i.product_name}`).join(', ')}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-destructive">
                        R$ {purchase.total_cost.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Purchases;
